import React, { useState, useEffect, useContext } from "react";
import { DataContext } from "../../context/DataContext";
import { trackEvent } from "../../utils/telemetry";
import {
  Flex,
  Text,
  Group,
  Button,
  Stack,
  Divider,
  Center,
  Loader,
  Box,
  ThemeIcon,
  Drawer,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import QRCode from "react-qr-code";
import { IconInfoCircle } from "@tabler/icons-react";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import useLocale from "../../hooks/useLocale";
import { useNavigate } from "react-router-dom";

export default function Handoff({ nextPath, urlOverride }) {
  const { settings, dispatch, data, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("handoff");
  const navigate = useNavigate();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [url, setUrl] = useState("");
  const [qrFound, setQrFound] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [shouldGetQrStatus, setShouldGetQrStatus] = useState(true);
  const [hasNotifiedHandoff, setHasNotifiedHandoff] = useState(false);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;

  // Custom handler for edge mode to detect HANDED_OFF state
  const handleEdgeStatus = (result) => {
    const { process_state: processState, status } = result || {};

    // Until the user actually hands off (`qrFound`), suppress
    // terminal status handling. Otherwise the backend's residual
    // FAIL/PASS from the previous journey step (e.g. a COPPA
    // failure the user just advanced past via showTransitionOnFail)
    // would fire here as if it were this step's result, bouncing
    // the user back to the transition screen.
    if (!qrFound) {
      if (processState === "HANDED_OFF") {
        setQrFound(true);

        // Send postMessage that user has moved to mobile device
        if (postPayloadMessage && !hasNotifiedHandoff) {
          setHasNotifiedHandoff(true);
          postPayloadMessage({
            event: "USER_MOVED_TO_MOBILE",
            app: nextPath || "handoff",
          });
        }
      }
      return true; // Keep polling — ignore stale terminal state
    }

    // From here on, the user has handed off to mobile, so any
    // terminal status belongs to this step.
    if (processState === "COMPLETED") {
      if (status === "PASS") {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        return false; // Stop polling
      }
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    // If status is PASS or FAIL without COMPLETED state
    if (status === "PASS") {
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    if (status === "FAIL") {
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    // Continue polling
    return true;
  };

  // Both hooks must be called unconditionally (React rules of hooks)
  // but trigger conditions ensure only one actually polls
  useEdgeStatusCheck(isEdgeMode, handleEdgeStatus, true);
  useStatusCheck(!isEdgeMode && qrFound, true, "handoff");

  // Handoff funnel on the original device: QR displayed → QR scanned.
  // The receiving device's own `landed` (device_role: handoff) is the
  // third step.
  useEffect(() => {
    trackEvent("handoff_shown", { path: nextPath || "handoff" }, { once: true });
  }, []);
  useEffect(() => {
    if (qrFound) {
      trackEvent("handoff_scanned", { path: nextPath || "handoff" }, { once: true });
    }
  }, [qrFound]);

  // Send postMessage when QR is scanned in non-edge mode
  useEffect(() => {
    if (qrFound && postPayloadMessage && !hasNotifiedHandoff) {
      setHasNotifiedHandoff(true);
      postPayloadMessage({
        event: "USER_MOVED_TO_MOBILE",
        app: nextPath || "handoff",
      });
    }
  }, [qrFound, postPayloadMessage, nextPath, hasNotifiedHandoff]);

  useEffect(() => {
    // Get all current URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const frontendParam = urlParams.get('frontend');
    const edgeParam = urlParams.get('edge');

    // Preserve all existing query parameters
    urlParams.set('qr', 'true');
    // Mobile handoff receiver has already seen the verification
    // notice on desktop — carry a flag so App.js can bypass it
    // and drop the user straight into the journey.
    urlParams.set('notice_shown', 'true');
    if (nextPath) {
      urlParams.set('nextPath', nextPath);
    }
    // Preserve edge parameter if it exists
    if (edgeParam) {
      urlParams.set('edge', edgeParam);
    }
    // Pass selected test case to handoff device
    if (data?.selected_test_case_id) {
      urlParams.set('test_case_id', data.selected_test_case_id);
    }
    const queryString = urlParams.toString();

    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname.startsWith("127.0.0.") ||
      hostname.startsWith("0.0.0.0");

    if (isLocalhost) {
      if (urlOverride) {
        setUrl(urlOverride);
      } else {
        setUrl(
          `https://43e1b5838093.ngrok.app/${settings.request_id}?${queryString}`
        );
      }
    } else if (hostname.endsWith("fe-dcams.pages.dev")) {
      // For Cloudflare Pages preview domains
      setUrl(`${window.location.origin}/${settings.request_id}?${queryString}`);
    } else {
      // Determine frontend to use based on hostname or query param
      const useVxFrontend = hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";

      let urlToUse;
      if (useVxFrontend) {
        urlToUse = `https://vx-fe.dcams.app/${settings.request_id}?${queryString}`;
      } else if (hostname.endsWith("dcams.app")) {
        urlToUse = `${window.location.origin}/${settings.request_id}?${queryString}`;
      } else {
        urlToUse = `${process.env.REACT_APP_FRONTEND_URL}/${settings.request_id}?${queryString}`;
      }
      setUrl(urlToUse);
    }
  }, [settings, nextPath, urlOverride]);

  useEffect(() => {
    // Skip QR polling for edge mode - edge status hook handles everything
    if (isEdgeMode) {
      return;
    }

    const fetchQrStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
          {
            method: "POST",
            body: JSON.stringify({
              request_id: settings.request_id,
              type: "get",
              site_name: settings.site_name,
            }),
          }
        );
        const data = await response.json();

        if (data.found) {
          setQrFound(true); // Stop fetching once QR is found
        }
      } catch (error) {
        console.error("Error fetching QR status:", error);
      }
    };

    if (!qrFound) {
      const intervalId = setInterval(fetchQrStatus, 6000);
      return () => clearInterval(intervalId);
    }
  }, [qrFound, settings, isEdgeMode]);

  useEffect(() => {
    const timerId = setTimeout(
      () => {
        setIsTimeUp(true);
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearTimeout(timerId);
  }, []);

  const restartFetch = () => {
    setIsTimeUp(false);
    setShouldFetch(true);
    setShouldGetQrStatus(true);
  };

  // Get resolved colors from theme or data styles
  const resolvedPrimaryColor = data?.styles?.primaryColor ?? theme.primaryColor;
  const buttonTextColor = data?.styles?.components?.button?.color || "white";

  // In-progress view when QR has been scanned
  if (qrFound) {
    return (
      <Stack
        align="center"
        justify="center"
        style={{
          height: "100%",
          minHeight: "400px",
          padding: "40px 20px",
        }}
      >
        <Loader size="lg" />
        <Stack spacing={4} align="center" mt="md">
          <Text fz="xl" fw={700} align="center">
            {locale?.inProgress?.title || "Verification in Progress"}
          </Text>
          <Text c="dimmed" align="center" fz="sm">
            {locale?.inProgress?.subtitle || "Please complete the verification on your mobile device"}
          </Text>
        </Stack>
        {isTimeUp && (
          <Button variant="outline" onClick={restartFetch} mt="lg">
            {locale?.inProgress?.button || "Refresh Status"}
          </Button>
        )}
      </Stack>
    );
  }

  // Main handoff view with QR code
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "500px",
      }}
    >
      {/* Main content area */}
      <Stack
        spacing="lg"
        style={{
          flex: 1,
          padding: "20px",
        }}
      >
        {/* Header */}
        <Stack spacing={0}>
          <Text fz={24} fw={700}>
            {locale?.title || "Continue on Your Phone"}
          </Text>
          <Text c="dimmed" fz="sm">
            {locale?.subtitle || "To continue the verification process, follow these steps:"}
          </Text>
        </Stack>

        {/* Steps and QR code section */}
        <Flex
          gap="md"
          align="flex-start"
          justify="space-between"
          wrap="wrap"
        >
          {/* Steps on the left with timeline */}
          <Box style={{ flex: 1, minWidth: 200 }}>
            {/* Step 1 */}
            <Group noWrap spacing="sm" align="flex-start">
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ThemeIcon
                  size={28}
                  radius="xl"
                  variant="filled"
                  color={resolvedPrimaryColor}
                >
                  <Text fz="sm" fw={600} color={buttonTextColor}>1</Text>
                </ThemeIcon>
                <Box
                  style={{
                    width: 2,
                    height: 14,
                    backgroundColor: theme.fn.primaryColor(),
                    opacity: 0.3,
                    marginTop: 4,
                  }}
                />
              </Box>
              <Text fz="sm" style={{ lineHeight: 1.4, paddingTop: 4 }}>
                {locale?.steps?.one || "Open your phone's camera app"}
              </Text>
            </Group>

            {/* Step 2 */}
            <Group noWrap spacing="sm" align="flex-start" mt={4}>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ThemeIcon
                  size={28}
                  radius="xl"
                  variant="filled"
                  color={resolvedPrimaryColor}
                >
                  <Text fz="sm" fw={600} color={buttonTextColor}>2</Text>
                </ThemeIcon>
                <Box
                  style={{
                    width: 2,
                    height: 14,
                    backgroundColor: theme.fn.primaryColor(),
                    opacity: 0.3,
                    marginTop: 4,
                  }}
                />
              </Box>
              <Text fz="sm" style={{ lineHeight: 1.4, paddingTop: 4 }}>
                {locale?.steps?.two || "Scan the QR code"}
              </Text>
            </Group>

            {/* Step 3 */}
            <Group noWrap spacing="sm" align="flex-start" mt={4}>
              <ThemeIcon
                size={28}
                radius="xl"
                variant="filled"
                color={resolvedPrimaryColor}
              >
                <Text fz="sm" fw={600} color={buttonTextColor}>3</Text>
              </ThemeIcon>
              <Text fz="sm" style={{ lineHeight: 1.4, paddingTop: 4 }}>
                {locale?.steps?.three || "Visit the link and complete verification"}
              </Text>
            </Group>
          </Box>

          {/* QR Code on the right (wraps below steps in narrow containers) */}
          <Box style={{ flexShrink: 0, margin: "0 auto" }}>
            <QRCode
              size={160}
              value={url}
              viewBox="0 0 256 256"
              fgColor={theme.colorScheme === "dark" ? "#ffffff" : "#000000"}
              bgColor="transparent"
            />
          </Box>
        </Flex>

      </Stack>

      {/* About button - pushed to bottom */}
      <Box px="md" pb="md">
        {/* Keep window open notice */}
        <Text fz="xs" c="dimmed" fs="italic" mb="xs" align="center">
          {locale?.keepOpen || "Keep this window open while you complete verification on your phone."}
        </Text>
        {settings.journey?.settings?.allowUpload && (
          <Button
            variant="subtle"
            size="xs"
            fullWidth
            onClick={() => navigate("/upload")}
            mb="xs"
          >
            {locale?.continueOnDevice || "I would rather continue on this device"}
          </Button>
        )}
        <Divider mb="xs" />
        <Button
          variant="subtle"
          color={resolvedPrimaryColor}
          fullWidth
          leftIcon={<IconInfoCircle size={18} />}
          onClick={openDrawer}
          styles={{
            root: {
              justifyContent: "flex-start",
              color: theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[6],
              "& svg": {
                color: theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[6],
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
            label: { fontWeight: 600 },
          }}
        >
          {locale?.aboutTitle || "Why do I need to use my phone?"}
        </Button>
      </Box>

      {/* About drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        position="bottom"
        size="auto"
        title={
          <Group spacing="xs" noWrap>
            <IconInfoCircle size={20} />
            <Text fw={600}>{locale?.aboutTitle || "Why do I need to use my phone?"}</Text>
          </Group>
        }
        styles={{
          inner: {
            padding: "0 8px 8px 8px",
          },
          content: {
            borderRadius: "16px !important",
            overflow: "hidden",
          },
          body: {
            padding: "20px",
          },
          header: {
            padding: "20px 20px 0 20px",
          },
          close: {
            color: theme.colorScheme === "dark" ? theme.white : theme.black,
          },
        }}
      >
        <Text fz="sm">
          {locale?.aboutDescription || "Your phone's camera provides the best experience for identity verification. It allows us to securely capture and verify your information with higher accuracy than a desktop webcam."}
        </Text>
      </Drawer>

      {/* Refresh button when timed out */}
      {isTimeUp && (
        <Center pb="md">
          <Button variant="outline" onClick={restartFetch}>
            {locale?.inProgress?.button || "Refresh Status"}
          </Button>
        </Center>
      )}
    </Box>
  );
}
